<template>
  <div
    ref="racine"
    class="maintenance-page ecran-public"
    :class="{ 'maintenance-page--embarquee': embarquee }"
    data-testid="maintenance-page"
    :data-embarquee="embarquee ? 'oui' : 'non'"
  >
    <PublicBrandBar logo="/logo-alertis.png" alt="Alertis Formation" />

    <div class="maintenance-container">
      <PublicCard>
        <PublicOutcome
          tone="warning"
          icon="fas fa-gear"
          title="Mise à jour en cours"
          data-testid="maintenance-issue"
        >
          <p data-testid="maintenance-message">{{ message }}</p>
          <template #actions>
            <Button
              variant="primary"
              class="public-submit"
              :loading="checking"
              data-testid="maintenance-reessayer"
              @click="retry"
            >
              {{ checking ? 'Vérification…' : 'Réessayer' }}
            </Button>
          </template>
        </PublicOutcome>
      </PublicCard>

      <PublicContact email="info@alertis.fr" phone="04 78 90 46 30" />
    </div>
  </div>
</template>

<script setup>
/**
 * ÉCRAN DE MAINTENANCE — plein écran, par-dessus toute l'application, embeds
 * compris (monté par `App.vue` dès qu'un appel renvoie le 503 du middleware).
 *
 * Écran public (docs/PUBLIQUES-V2.md) : la personne qui le voit peut être un
 * commercial comme un visiteur d'alertis.fr dans l'iframe du formulaire. Il se
 * pose donc sur le cadre des écrans publics — barre de marque, carte, issue au
 * ton « attente », une seule action, à qui s'adresser — et non plus sur le
 * dégradé indigo et les engrenages en rotation infinie (P13, P23).
 *
 * DANS UNE IFRAME, le cadre de l'embed ne bouge pas (P16) : l'écran est en
 * `position: fixed` et défile dans son propre calque ; il ne pèse rien dans la
 * hauteur de la page que mesure `useIframeHeight`. L'iframe garde donc la
 * hauteur du formulaire (plus de 2 000 px) : centré, le message tomberait au
 * milieu de ce cadre, loin sous la ligne de flottaison du site hôte. Dans une
 * iframe, il se pose en haut.
 *
 * « Réessayer » demande d'abord au serveur si la maintenance est finie
 * (`checkMaintenanceOver`), et dit qu'il vérifie pendant ce temps.
 *
 * TOUT CE QUI EST DESSOUS DEVIENT INERTE. `App.vue` rend inerte le cadre de
 * l'application, frère de ce calque dans `#app` ; ce composant rend inerte le
 * reste de `<body>` — ce qui y est téléporté (popup d'appel, modales,
 * conteneurs de bandeaux) échappait au premier, et restait atteignable au
 * clavier sous l'écran (`isolerCalque`).
 */
import { onBeforeUnmount, onMounted, ref } from 'vue'
import Button from '../ui/Button.vue'
import { PublicBrandBar, PublicCard, PublicContact, PublicOutcome } from '../ui/public'
import { checkMaintenanceOver, DEFAULT_MAINTENANCE_MESSAGE } from '../../composables/useMaintenance.js'
import { isolerCalque } from '../../utils/isoler-calque.js'

defineProps({
  message: {
    type: String,
    default: DEFAULT_MAINTENANCE_MESSAGE
  }
})

const checking = ref(false)

const racine = ref(null)
let liberer = () => {}
onMounted(() => {
  liberer = isolerCalque(racine.value)
})
onBeforeUnmount(() => liberer())

/** Affichée dans l'iframe d'un site hôte (formulaire, carte) ? */
function isInIframe() {
  try {
    return window.self !== window.top
  } catch {
    // Accès à `top` refusé : on est forcément dans le cadre d'un autre site.
    return true
  }
}

const embarquee = isInIframe()

async function retry() {
  if (checking.value) return
  checking.value = true
  const reloading = await checkMaintenanceOver()
  // Page en cours de rechargement : l'attente reste affichée jusqu'au bout.
  if (!reloading) checking.value = false
}
</script>

<style scoped>
/* Calque plein écran : au-dessus de tout, et il défile seul quand le message
   est long ou l'iframe basse. */
.maintenance-page {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: var(--color-bg-secondary);
}

/* Le cadre de l'inscription (P6), centré dans la hauteur. */
.maintenance-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--spacing-6);
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: var(--spacing-8) var(--spacing-4) var(--spacing-16);
  box-sizing: border-box;
}

.maintenance-container > :deep(.pct) {
  margin-top: var(--spacing-2);
}

.maintenance-container :deep(.pou-actions) {
  align-self: stretch;
  flex-direction: column;
  align-items: center;
}

.maintenance-container :deep(.pou-actions .btn.public-submit) {
  max-width: 24rem;
}

/* Dans une iframe : en haut du cadre, là où le visiteur du site hôte regarde. */
.maintenance-page--embarquee .maintenance-container {
  flex: 0 0 auto;
  justify-content: flex-start;
}

@media (max-width: 768px) {
  .maintenance-container {
    padding: var(--spacing-5) var(--spacing-4) var(--spacing-12);
  }
}
</style>
